import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { dailyItemService } from "../services/authService";
import StatusConfirmationModal from "./StatusConfirmationModal";

const DailyItemModal = ({ item, users, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    assignedTo: "",
    startDate: "",
    endDate: "",
    deliveryTime: "09:00",
    category: "other",
    quantity: "1",
    scheduleQuantity: "1",
    price: 0,
    frequency: "daily",
    weeklyDays: [],
    monthlyDate: 1,
    isActive: true,
    isIndefinite: false,
  });
  const [loading, setLoading] = useState(false);
  const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
  const [statusConfirmationData, setStatusConfirmationData] = useState(null);

  useEffect(() => {
    if (item) {
      setFormData({
        title: item.title || "",
        description: item.description || "",
        assignedTo: item.assignedTo?._id || "",
        startDate: item.startDate
          ? new Date(item.startDate).toISOString().split("T")[0]
          : "",
        endDate: item.endDate
          ? new Date(item.endDate).toISOString().split("T")[0]
          : "",
        deliveryTime: item.deliveryTime || "09:00",
        category: item.category || "other",
        quantity: item.quantity || "1",
        scheduleQuantity: item.scheduleQuantity || "1",
        price: item.price || 0,
        frequency: item.frequency || "daily",
        weeklyDays: item.weeklyDays || [],
        monthlyDate: item.monthlyDate || 1,
        isActive: item.isActive !== undefined ? item.isActive : true,
        isIndefinite:
          item.isIndefinite !== undefined ? item.isIndefinite : false,
      });
    }
  }, [item]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newFormData = {
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    };

    // If indefinite is checked, clear the endDate
    if (name === "isIndefinite" && checked) {
      newFormData.endDate = "";
    }

    setFormData(newFormData);
  };

  const handleWeeklyDaysChange = (day) => {
    const updatedDays = formData.weeklyDays.includes(day)
      ? formData.weeklyDays.filter((d) => d !== day)
      : [...formData.weeklyDays, day];

    setFormData({
      ...formData,
      weeklyDays: updatedDays,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation
      if (!formData.title.trim()) {
        toast.error("Title is required");
        return;
      }
      if (!formData.assignedTo) {
        toast.error("Please select a user");
        return;
      }

      // Check user status before creating the item
      const selectedUser = users.find(
        (user) => user._id === formData.assignedTo
      );

      if (
        selectedUser &&
        selectedUser.status &&
        selectedUser.status !== "active"
      ) {
        const statusMessages = {
          inactive: "inactive",
          pending: "pending approval",
          suspended: "suspended",
        };

        const statusMessage =
          statusMessages[selectedUser.status] || selectedUser.status;

        // Show custom confirmation modal instead of browser confirm
        setStatusConfirmationData({
          user: selectedUser,
          statusMessage: statusMessage,
          formData: formData,
        });
        setShowStatusConfirmation(true);
        setLoading(false);
        return;
      }

      if (!formData.startDate) {
        toast.error("Start date is required");
        return;
      }
      if (!formData.isIndefinite) {
        if (!formData.endDate) {
          toast.error(
            "End date is required when not using indefinite delivery"
          );
          return;
        }
        if (new Date(formData.startDate) >= new Date(formData.endDate)) {
          toast.error("End date must be after start date");
          return;
        }
      }
      if (!formData.scheduleQuantity || formData.scheduleQuantity < 1) {
        toast.error("Schedule quantity is required and must be at least 1");
        return;
      }
      if (formData.frequency === "weekly" && formData.weeklyDays.length === 0) {
        toast.error("Please select at least one day for weekly frequency");
        return;
      }

      const submitData = { ...formData };

      // If we reach here, either the user is active or no status check is needed
      await createDailyItem(submitData);
    } catch (error) {
      console.error("Error saving daily item:", error);
      toast.error(error.response?.data?.message || "Failed to save daily item");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusConfirmation = async () => {
    if (!statusConfirmationData) return;

    setShowStatusConfirmation(false);
    setLoading(true);

    try {
      // Continue with the item creation using the stored form data
      await createDailyItem(statusConfirmationData.formData);
    } catch (error) {
      console.error("Error creating daily item after confirmation:", error);
      toast.error(
        error.response?.data?.message || "Failed to create daily item"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusConfirmationCancel = () => {
    setShowStatusConfirmation(false);
    setStatusConfirmationData(null);
    setLoading(false);
  };

  const createDailyItem = async (submitData) => {
    // Clean up endDate if indefinite delivery is selected
    if (submitData.isIndefinite) {
      delete submitData.endDate;
    }

    // Clean up data based on frequency
    if (submitData.frequency === "daily") {
      delete submitData.weeklyDays;
      delete submitData.monthlyDate;
    } else if (submitData.frequency === "weekly") {
      delete submitData.monthlyDate;
    } else if (submitData.frequency === "monthly") {
      delete submitData.weeklyDays;
    }

    if (item) {
      await dailyItemService.updateDailyItem(item._id, submitData);
      toast.success("Daily item updated successfully");
    } else {
      await dailyItemService.createDailyItem(submitData);
      toast.success("Daily item created successfully");
    }

    onSave();
  };

  const weekDays = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ];

  const categories = [
    { value: "milk", label: "Milk" },
    { value: "newspaper", label: "Newspaper" },
    { value: "groceries", label: "Groceries" },
    { value: "medicine", label: "Medicine" },
    { value: "other", label: "Other" },
  ];

  return (
    <div className="modal-overlay">
      <div
        className="modal"
        style={{
          maxWidth: "600px",
          maxHeight: "90vh",
          overflowY: "auto",
          background: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
          borderRadius: "20px",
          boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <div
          className="modal-header"
          style={{
            background: "linear-gradient(135deg, #4CAF50 0%, #45a049 100%)",
            borderRadius: "20px 20px 0 0",
            color: "white",
            padding: "25px 30px",
            borderBottom: "none",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div
              style={{
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "12px",
                padding: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: "24px" }}>{item ? "✏️" : "➕"}</span>
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "24px", fontWeight: "600" }}>
                {item ? "Edit Daily Item" : "Create Daily Item"}
              </h2>
              <p
                style={{
                  margin: "2px 0 0 0",
                  fontSize: "14px",
                  opacity: "0.9",
                }}
              >
                {item
                  ? "Modify delivery schedule and details"
                  : "Set up a new recurring delivery"}
              </p>
            </div>
          </div>
          <button
            className="btn-close"
            onClick={onClose}
            style={{
              background: "rgba(255, 255, 255, 0.2)",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              color: "white",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.3)")
            }
            onMouseLeave={(e) =>
              (e.target.style.background = "rgba(255, 255, 255, 0.2)")
            }
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ padding: "30px" }}>
            {/* Basic Information Section */}
            <div
              style={{
                marginBottom: "30px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "15px",
                border: "1px solid #e9ecef",
              }}
            >
              <h4
                style={{
                  margin: "0 0 20px 0",
                  color: "#495057",
                  fontSize: "18px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "20px" }}>📋</span>
                Basic Information
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div className="form-group">
                  <label
                    htmlFor="title"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Title <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="e.g., Daily Milk Delivery"
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  />
                </div>

                <div className="form-group">
                  <label
                    htmlFor="assignedTo"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Assign To User <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <select
                    id="assignedTo"
                    name="assignedTo"
                    value={formData.assignedTo}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                      backgroundColor: "white",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  >
                    <option value="">👤 Select User</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.fullName} ({user.uniqueUserId}) - {user.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label
                  htmlFor="description"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Additional details about the delivery item..."
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "10px",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                    outline: "none",
                    resize: "vertical",
                    minHeight: "80px",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                  onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                />
              </div>
            </div>

            {/* Item Details Section */}
            <div
              style={{
                marginBottom: "30px",
                padding: "20px",
                backgroundColor: "#fff3e0",
                borderRadius: "15px",
                border: "1px solid #ffb74d",
              }}
            >
              <h4
                style={{
                  margin: "0 0 20px 0",
                  color: "#e65100",
                  fontSize: "18px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "20px" }}>📦</span>
                Item Details
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div className="form-group">
                  <label
                    htmlFor="category"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Category
                  </label>
                  <select
                    id="category"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                      backgroundColor: "white",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#ff9800")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  >
                    {categories.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.value === "milk" && "🥛"}
                        {cat.value === "newspaper" && "📰"}
                        {cat.value === "groceries" && "🛒"}
                        {cat.value === "medicine" && "💊"}
                        {cat.value === "other" && "📦"} {cat.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label
                    htmlFor="deliveryTime"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    🕘 Delivery Time
                  </label>
                  <input
                    type="time"
                    id="deliveryTime"
                    name="deliveryTime"
                    value={formData.deliveryTime}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#ff9800")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  />
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                }}
              >
                <div className="form-group">
                  <label
                    htmlFor="quantity"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    📏 Quantity
                  </label>
                  <input
                    type="text"
                    id="quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    placeholder="e.g., 1 liter, 2 bottles"
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#ff9800")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  />
                </div>

                <div className="form-group">
                  <label
                    htmlFor="price"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    💰 Price (₹)
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#ff9800")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  />
                </div>
              </div>
            </div>

            {/* Schedule Configuration Section */}
            <div
              style={{
                marginBottom: "30px",
                padding: "20px",
                backgroundColor: "#e8f5e8",
                borderRadius: "15px",
                border: "1px solid #81c784",
              }}
            >
              <h4
                style={{
                  margin: "0 0 20px 0",
                  color: "#2e7d32",
                  fontSize: "18px",
                  fontWeight: "600",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ fontSize: "20px" }}>📅</span>
                Schedule Configuration
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "20px",
                  marginBottom: "20px",
                }}
              >
                <div className="form-group">
                  <label
                    htmlFor="startDate"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Start Date <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    required
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  />
                </div>

                <div className="form-group">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "10px",
                    }}
                  >
                    <input
                      type="checkbox"
                      id="isIndefinite"
                      name="isIndefinite"
                      checked={formData.isIndefinite}
                      onChange={handleInputChange}
                      style={{ marginRight: "10px" }}
                    />
                    <label
                      htmlFor="isIndefinite"
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                    >
                      Indefinite Delivery (no end date)
                    </label>
                  </div>

                  <div
                    style={{
                      display: formData.isIndefinite ? "none" : "block",
                    }}
                  >
                    <label
                      htmlFor="endDate"
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "600",
                        color: "#333",
                        fontSize: "14px",
                      }}
                    >
                      End Date{" "}
                      {!formData.isIndefinite && (
                        <span style={{ color: "#dc3545" }}>*</span>
                      )}
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      disabled={formData.isIndefinite}
                      required={!formData.isIndefinite}
                      style={{
                        width: "100%",
                        padding: "12px 15px",
                        border: "2px solid #e0e0e0",
                        borderRadius: "10px",
                        fontSize: "14px",
                        transition: "all 0.3s ease",
                        outline: "none",
                        opacity: formData.isIndefinite ? "0.6" : "1",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                      onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                    />
                    {formData.isIndefinite && (
                      <p
                        style={{
                          color: "#666",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        Delivery logs will be generated dynamically up to 1
                        month in the future.
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="frequency"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  🔄 Delivery Frequency
                </label>
                <select
                  id="frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "10px",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                    outline: "none",
                    backgroundColor: "white",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                  onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                >
                  <option value="daily">📅 Daily</option>
                  <option value="weekly">📅 Weekly</option>
                  <option value="monthly">📅 Monthly</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "20px" }}>
                <label
                  htmlFor="scheduleQuantity"
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontWeight: "600",
                    color: "#333",
                    fontSize: "14px",
                  }}
                >
                  📦 Schedule Quantity{" "}
                  <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <input
                  type="number"
                  id="scheduleQuantity"
                  name="scheduleQuantity"
                  value={formData.scheduleQuantity}
                  onChange={handleInputChange}
                  min="1"
                  step="1"
                  placeholder="e.g., 2 (quantity for each delivery)"
                  required
                  style={{
                    width: "100%",
                    padding: "12px 15px",
                    border: "2px solid #e0e0e0",
                    borderRadius: "10px",
                    fontSize: "14px",
                    transition: "all 0.3s ease",
                    outline: "none",
                    backgroundColor: "white",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                  onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                />
                <small
                  style={{
                    display: "block",
                    marginTop: "5px",
                    color: "#6c757d",
                    fontSize: "12px",
                    fontStyle: "italic",
                  }}
                >
                  This quantity will be used for each scheduled delivery. The
                  quantity in Item Details is for pricing only.
                </small>
              </div>

              {formData.frequency === "weekly" && (
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "12px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    Weekly Delivery Days{" "}
                    <span style={{ color: "#dc3545" }}>*</span>
                  </label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(120px, 1fr))",
                      gap: "12px",
                      padding: "15px",
                      backgroundColor: "white",
                      borderRadius: "10px",
                      border: "2px solid #e0e0e0",
                    }}
                  >
                    {weekDays.map((day) => (
                      <label
                        key={day.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          backgroundColor: formData.weeklyDays.includes(
                            day.value
                          )
                            ? "#4CAF50"
                            : "#f8f9fa",
                          color: formData.weeklyDays.includes(day.value)
                            ? "white"
                            : "#333",
                          transition: "all 0.3s ease",
                          fontWeight: "500",
                          fontSize: "13px",
                        }}
                        onMouseEnter={(e) => {
                          if (!formData.weeklyDays.includes(day.value)) {
                            e.target.style.backgroundColor = "#e8f5e8";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!formData.weeklyDays.includes(day.value)) {
                            e.target.style.backgroundColor = "#f8f9fa";
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.weeklyDays.includes(day.value)}
                          onChange={() => handleWeeklyDaysChange(day.value)}
                          style={{
                            marginRight: "8px",
                            width: "16px",
                            height: "16px",
                          }}
                        />
                        {day.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {formData.frequency === "monthly" && (
                <div className="form-group" style={{ marginBottom: "20px" }}>
                  <label
                    htmlFor="monthlyDate"
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontWeight: "600",
                      color: "#333",
                      fontSize: "14px",
                    }}
                  >
                    📅 Monthly Delivery Date
                  </label>
                  <select
                    id="monthlyDate"
                    name="monthlyDate"
                    value={formData.monthlyDate}
                    onChange={handleInputChange}
                    style={{
                      width: "100%",
                      padding: "12px 15px",
                      border: "2px solid #e0e0e0",
                      borderRadius: "10px",
                      fontSize: "14px",
                      transition: "all 0.3s ease",
                      outline: "none",
                      backgroundColor: "white",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#4CAF50")}
                    onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map((date) => (
                      <option key={date} value={date}>
                        {date}
                        {date === 1
                          ? "st"
                          : date === 2
                          ? "nd"
                          : date === 3
                          ? "rd"
                          : "th"}{" "}
                        of each month
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    padding: "15px",
                    backgroundColor: "white",
                    borderRadius: "10px",
                    border: "2px solid #e0e0e0",
                    transition: "all 0.3s ease",
                    fontWeight: "500",
                  }}
                  onMouseEnter={(e) =>
                    (e.target.style.backgroundColor = "#f8f9fa")
                  }
                  onMouseLeave={(e) =>
                    (e.target.style.backgroundColor = "white")
                  }
                >
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    style={{
                      marginRight: "12px",
                      width: "18px",
                      height: "18px",
                    }}
                  />
                  <div>
                    <div style={{ color: "#333", fontWeight: "600" }}>
                      ✅ Active Delivery Schedule
                    </div>
                    <div
                      style={{
                        color: "#666",
                        fontSize: "12px",
                        marginTop: "2px",
                      }}
                    >
                      Generate delivery schedules automatically for this item
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          <div
            className="modal-footer"
            style={{
              padding: "25px 30px",
              borderTop: "2px solid #e9ecef",
              background: "rgba(255, 255, 255, 0.8)",
              borderRadius: "0 0 20px 20px",
              display: "flex",
              gap: "15px",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              style={{
                padding: "12px 25px",
                fontWeight: "500",
                borderRadius: "10px",
                border: "2px solid #6c757d",
                background: "transparent",
                color: "#6c757d",
                cursor: "pointer",
                transition: "all 0.3s ease",
                fontSize: "14px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#6c757d";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#6c757d";
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                background: loading
                  ? "#ccc"
                  : "linear-gradient(135deg, #4CAF50, #45a049)",
                border: "none",
                borderRadius: "10px",
                padding: "12px 25px",
                fontSize: "14px",
                color: "white",
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: "600",
                transition: "all 0.2s ease",
                boxShadow: loading
                  ? "none"
                  : "0 4px 8px rgba(76, 175, 80, 0.3)",
                minWidth: "140px",
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.transform = "translateY(-2px)";
                  e.target.style.boxShadow =
                    "0 6px 12px rgba(76, 175, 80, 0.4)";
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.transform = "translateY(0)";
                  e.target.style.boxShadow = "0 4px 8px rgba(76, 175, 80, 0.3)";
                }
              }}
            >
              {loading ? (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span>⏳</span>
                  Saving...
                </span>
              ) : (
                <span
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span>{item ? "✏️" : "➕"}</span>
                  {item ? "Update Item" : "Create Item"}
                </span>
              )}
            </button>
          </div>
        </form>

        {showStatusConfirmation && statusConfirmationData && (
          <StatusConfirmationModal
            isOpen={showStatusConfirmation}
            onClose={handleStatusConfirmationCancel}
            onConfirm={handleStatusConfirmation}
            userName={`${statusConfirmationData.user.firstName} ${statusConfirmationData.user.lastName}`}
            userStatus={statusConfirmationData.user.status}
            statusMessage={statusConfirmationData.statusMessage}
          />
        )}
      </div>
    </div>
  );
};

export default DailyItemModal;
